import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Avatar,
  Card,
  CardContent,
  useTheme,
  Skeleton,
  Tooltip,
  TextField,
  CircularProgress,
  AvatarGroup,
  Fade,
  Popover,
  Checkbox,
  InputBase,
  ClickAwayListener,
  ListItemIcon,
  Divider,
} from "@mui/material";
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

// Icons
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LoopOutlinedIcon from "@mui/icons-material/LoopOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import BlockIcon from "@mui/icons-material/Block";
import LinkIcon from "@mui/icons-material/Link";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import { Search, Clear } from "@mui/icons-material";

import { BugItem, Sprint } from "./BacklogTab";
import { hexToRgba } from "@/Components/helpers/util";
import {
  colorPalette,
  severityColorMap,
  severityMap,
  typeToIconMapping,
} from "../utils";
import { useAppSelector } from "@/app/hooks";
import { selectApiData } from "@/features/bughurd/boardSelector";

// --- Types ---

interface TaskCardProps {
  task: BugItem;
  onTaskClick: (taskId: string) => void;
  onMenuClick: (event: React.MouseEvent, taskId: string) => void;
  isDragging?: boolean;
  availableStatus?: any;
  onTaskUpdate?: (
    taskId: string,
    updatedTask: any | null,
    field: string,
    containerId?: string
  ) => any;
  refreshColumn?: () => void;
  companyMemberData?: any;
  containerId?: string;
}

interface DroppableColumnProps {
  id: string;
  title: string;
  tasks: BugItem[];
  isLoading?: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  totalCount?: number;
  onTaskClick: (taskId: string) => void;
  onMenuClick: (event: React.MouseEvent, taskId: string) => void;
  onCreateTask?: () => void;
  onLoadMore?: (columnId: string) => void;
  headerActions?: React.ReactNode;
  emptyMessage?: string;
  sprint?: Sprint;
  isBacklog?: boolean;
  color?: string;
  canEditColumn?: boolean;
  onTitleChange?: (newTitle: string) => void;
  onDelete?: () => void;
  dragHandleProps?: any;
  availableStatus?: any;
  onTaskUpdate?: (
    taskId: string,
    updatedTask: any | null,
    field: string,
    containerId?: string
  ) => any;
  refreshColumn?: () => void;
  companyMemberData?: any;
  isMinimized?: boolean;
  onToggleMinimize?: () => void;
  sprints?: Sprint[];
  isDragging?: boolean;
  containerId: string;
}

interface BacklogBoardViewProps {
  backlogRows: BugItem[];
  sprints: Sprint[];
  sprintTasksData: { [key: string]: any };
  isLoading: boolean;
  onTaskClick: (taskId: string) => void;
  onMenuClick: (event: React.MouseEvent, taskId: string) => void;
  onCreateTask: (sprintId?: string) => void;
  onCreateSprint: (event: React.MouseEvent) => void;
  onStartSprint: (event: React.MouseEvent, sprint: Sprint) => void;
  onCompleteSprint: (event: React.MouseEvent, sprint: Sprint) => void;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragOver: (event: DragOverEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  activeTask?: BugItem | null;
  findTask: (taskId: string) => BugItem | undefined;
  statusList?: any;
  companyMemberData?: any;
  onTaskUpdate?: (
    taskId: string,
    updatedTask: any | null,
    field: string,
    containerId?: string
  ) => any;
  refreshColumn?: () => void;
  minimizedColumns?: { [key: string]: boolean };
  onToggleMinimize?: (columnId: string) => void;
  onLoadMore?: (columnId: string) => void;
  columnPaginationState?: {
    [columnId: string]: { page: number; limit: number; isLoadingMore: boolean };
  };
  backlogTotalCount?: number;
  sprintTotalCounts?: { [sprintId: string]: number };
}

// --- Utility Functions ---

const getPriorityColor = (severity: string) => {
  return severityColorMap[severity] || "#757575";
};

const getStatusColor = (statusCode: string) => {
  switch (statusCode?.toLowerCase()) {
    case "todo":
      return "#757575";
    case "inprogress":
      return "#2196f3";
    case "done":
      return "#4caf50";
    case "blocked":
      return "#f44336";
    default:
      return "#757575";
  }
};

const formatDate = (
  dateString: string,
  format: "short" | "relative" = "short"
) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (format === "relative") {
    if (isNaN(date.getTime())) return "";
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0)
      return `Today at ${date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    if (diffDays === 1)
      return `Yesterday at ${date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString([], {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const getInitials = (name: string) =>
  name
    ?.split(" ")
    ?.map((part) => part[0])
    ?.join("")
    ?.toUpperCase()
    ?.slice(0, 2) || "";

// --- Task Card Component ---
const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onTaskClick,
  onMenuClick,
  isDragging = false,
  onTaskUpdate,
  companyMemberData,
  containerId,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(["bugherd"]);
  const { projectDetails } = useAppSelector(selectApiData);

  const [isHovering, setIsHovering] = useState<boolean>(false);
  const [editingTitle, setEditingTitle] = useState<boolean>(false);
  const [titleValue, setTitleValue] = useState<string>(task?.summary || "");
  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLElement | null>(null);
  const [assigneeAnchorEl, setAssigneeAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [priorityAnchorEl, setPriorityAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [searchQuery, setSearchQuery] = useState<string>("");
  const titleInputRef = useRef<HTMLInputElement>(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({
    id: task._id,
    data: { type: "task", task },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    // opacity: isSortableDragging ? 0.5 : 1,
    opacity: 1,
  };

  // FIXED: Function to filter users based on project
  const getProjectFilteredUsers = () => {
    const allCompanyUsers = companyMemberData;

    if (!allCompanyUsers || !Array.isArray(allCompanyUsers)) {
      return [];
    }

    if (!projectDetails?.users) {
      return allCompanyUsers.filter(
        (x: any) => x._id === projectDetails?.owner
      );
    }

    // Extract userIds from project users (using userId field, not _id)
    const projectUserIds = projectDetails.users.map((user: any) => {
      if (typeof user === "object") {
        return user?.userId;
      }
      return user;
    });

    const projectOwnerId = projectDetails.owner;

    // Find the owner's userId from company members
    const ownerUser = allCompanyUsers.find(
      (member: any) => member._id === projectOwnerId
    );
    const ownerUserId = ownerUser?.userId;

    // Combine all userIds (both from project.users and owner)
    const allUserIds =
      ownerUserId && !projectUserIds.includes(ownerUserId)
        ? [...projectUserIds, ownerUserId]
        : projectUserIds;

    // Filter company members by userId
    const result = allCompanyUsers.filter((member: any) =>
      allUserIds.includes(member.userId)
    );

    return result;
  };

  const isDueDateApproaching = (): boolean => {
    if (!task?.dueDate) return false;
    const dueDate = new Date(task.dueDate);
    const today = new Date();
    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 2 && diffDays >= 0;
  };

  const typeInfo = task?.type
    ? typeToIconMapping[task.type]
    : typeToIconMapping["TASK"];

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingTitle(true);
    setTitleValue(task?.summary || "");
  };

  const handleSaveTitleEdit = async () => {
    if (!titleValue.trim()) {
      setEditingTitle(false);
      return;
    }

    try {
      if (onTaskUpdate) {
        await onTaskUpdate(
          task._id,
          { ...task, summary: titleValue },
          "summary",
          containerId
        );
        setEditingTitle(false);
      }
    } catch (error) {
      console.error("Error updating title:", error);
    }
  };

  const handleDueDateClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDateAnchorEl(e.currentTarget as HTMLElement);
  };

  const handleSeverityClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPriorityAnchorEl(e.currentTarget as HTMLElement);
  };

  const handleAssigneesClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAssigneeAnchorEl(e.currentTarget as HTMLElement);
  };

  const handlePriorityChange = async (newPriority: string) => {
    try {
      if (onTaskUpdate) {
        setPriorityAnchorEl(null);
        await onTaskUpdate(
          task._id,
          { ...task, severity: newPriority },
          "severity",
          containerId
        );
      }
    } catch (error) {
      setPriorityAnchorEl(null);
      console.error("Error updating priority:", error);
    }
  };

  const handleAssigneeChange = async (userId: string) => {
    try {
      const currentAssignees = task.assignTo || [];
      const userIndex = currentAssignees.findIndex(
        (u: any) => u._id === userId
      );

      let updatedAssignees;
      if (userIndex >= 0) {
        updatedAssignees = currentAssignees.filter(
          (u: any) => u._id !== userId
        );
      } else {
        const userToAdd = getProjectFilteredUsers()?.find(
          (u: any) => u._id === userId
        );
        if (userToAdd) {
          updatedAssignees = [
            ...currentAssignees,
            { _id: userId, fullName: userToAdd.fullName },
          ];
        } else {
          updatedAssignees = currentAssignees;
        }
      }
      setAssigneeAnchorEl(null);
      if (onTaskUpdate) {
        await onTaskUpdate(
          task._id,
          { ...task, assignTo: updatedAssignees },
          "assignTo",
          containerId
        );
      }
    } catch (error) {
      setAssigneeAnchorEl(null);
      console.error("Error updating assignees:", error);
    }
  };

  // FIXED: Use project filtered users
  const filteredUsers =
    getProjectFilteredUsers()?.filter((user: any) =>
      user.fullName.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

  const subtasksCount = task?.subTasks?.length || 0;

  const getDependencyCounts = (linkBugs: any[], relatedBugsCount: number) => {
    const counts = {
      waiting: 0,
      blocked: 0,
      linked: 0,
      related: relatedBugsCount,
    };
    linkBugs?.forEach((linkBug) => {
      switch (linkBug.type) {
        case "Waiting":
          counts.waiting++;
          break;
        case "Blocked":
          counts.blocked++;
          break;
        case "Linked":
          counts.linked++;
          break;
      }
    });
    return counts;
  };

  const getDependencyIcon = (type: string) => {
    switch (type) {
      case "waiting":
        return <ErrorOutlineIcon sx={{ fontSize: 12, color: "#ff9800" }} />;
      case "blocked":
        return <BlockIcon sx={{ fontSize: 12, color: "#f44336" }} />;
      case "linked":
        return <LinkIcon sx={{ fontSize: 12, color: "#2196f3" }} />;
      case "related":
        return (
          <PeopleAltOutlinedIcon sx={{ fontSize: 12, color: "#9c27b0" }} />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Card
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        onClick={() => onTaskClick(task._id)}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        sx={{
          mb: 2,
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          "&:hover": { boxShadow: 3 },
          cursor: isDragging ? "grabbing" : "grab",
          backgroundColor: theme.palette.background.paper,
          border: "1px solid #efefef",
          overflow: "visible",
          position: "relative",
          transition: "box-shadow 0.2s ease",
        }}
      >
        <Fade in={isHovering}>
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              display: "flex",
              backgroundColor: theme.palette.background.paper,
              borderRadius: "4px",
              border: `1px solid ${theme.palette.divider}`,
              padding: "2px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
              zIndex: 2,
            }}
          >
            <Tooltip title={t("Edit title")}>
              <IconButton
                size="small"
                onClick={handleEdit}
                sx={{ padding: "4px" }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("More options")}>
              <IconButton
                size="small"
                onClick={(e) => onMenuClick(e, task._id)}
                sx={{ padding: "4px" }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Fade>

        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 }, fontSize: "13px" }}>
          {/* Priority indicator */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1.5,
              cursor: "pointer",
            }}
            onClick={handleSeverityClick}
          >
            <FiberManualRecordIcon
              sx={{
                fontSize: 12,
                color: getPriorityColor(task?.severity),
                mr: 0.5,
              }}
            />
            <Typography
              sx={{
                fontSize: "12px",
                color: getPriorityColor(task?.severity),
                fontWeight: 500,
              }}
            >
              {severityMap[task?.severity] || ""}
            </Typography>
          </Box>

          {/* Task title */}
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", mb: 0.75 }}>
              {task?.type && (
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "3px",
                    backgroundColor: typeInfo?.bgcolor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 1,
                    mt: 0.3,
                    flexShrink: 0,
                  }}
                >
                  {typeInfo?.icon}
                </Box>
              )}

              {editingTitle ? (
                <ClickAwayListener onClickAway={handleSaveTitleEdit}>
                  <TextField
                    fullWidth
                    variant="standard"
                    value={titleValue}
                    onChange={(e) => setTitleValue(e.target.value)}
                    inputRef={titleInputRef}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveTitleEdit();
                      if (e.key === "Escape") setEditingTitle(false);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ flex: 1 }}
                  />
                </ClickAwayListener>
              ) : (
                <Tooltip title={task?.summary}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "text.primary",
                      wordBreak: "break-word",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      lineHeight: 1.4,
                    }}
                  >
                    {task?.bugKey && (
                      <span style={{ marginRight: "4px", color: "#757575" }}>
                        {task.bugKey}:
                      </span>
                    )}
                    {task?.summary}
                  </Typography>
                </Tooltip>
              )}
            </Box>

            {/* Dependencies Section */}
            {(task?.linkBugs?.length > 0 || task?.relatedBugsCount > 0) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 1,
                  flexWrap: "wrap",
                }}
              >
                {(() => {
                  const counts = getDependencyCounts(
                    task.linkBugs || [],
                    task.relatedBugsCount || 0
                  );
                  return (
                    <>
                      {counts.waiting > 0 && (
                        <Tooltip title={t("Waiting on dependencies")} arrow>
                          <Chip
                            icon={getDependencyIcon("waiting")}
                            label={counts.waiting}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "10px",
                              backgroundColor: "#fff3e0",
                              color: "#f57c00",
                              cursor: "pointer",
                              "& .MuiChip-label": {
                                paddingLeft: "4px",
                                paddingRight: "6px",
                              },
                            }}
                          />
                        </Tooltip>
                      )}
                      {counts.blocked > 0 && (
                        <Tooltip title={t("Blocked dependencies")} arrow>
                          <Chip
                            icon={getDependencyIcon("blocked")}
                            label={counts.blocked}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "10px",
                              backgroundColor: "#ffebee",
                              color: "#d32f2f",
                              cursor: "pointer",
                              "& .MuiChip-label": {
                                paddingLeft: "4px",
                                paddingRight: "6px",
                              },
                            }}
                          />
                        </Tooltip>
                      )}
                      {counts.linked > 0 && (
                        <Tooltip title={t("Linked dependencies")} arrow>
                          <Chip
                            icon={getDependencyIcon("linked")}
                            label={counts.linked}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "10px",
                              backgroundColor: "#e3f2fd",
                              color: "#1976d2",
                              cursor: "pointer",
                              "& .MuiChip-label": {
                                paddingLeft: "4px",
                                paddingRight: "6px",
                              },
                            }}
                          />
                        </Tooltip>
                      )}
                      {counts.related > 0 && (
                        <Tooltip title={t("My consumers")} arrow>
                          <Chip
                            icon={getDependencyIcon("related")}
                            label={counts.related}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "10px",
                              backgroundColor: "#f3e5f5",
                              color: "#7b1fa2",
                              cursor: "pointer",
                              "& .MuiChip-label": {
                                paddingLeft: "4px",
                                paddingRight: "6px",
                              },
                            }}
                          />
                        </Tooltip>
                      )}
                    </>
                  );
                })()}
              </Box>
            )}
          </Box>

          {/* Bottom section with assignees and metadata */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
            }}
          >
            {/* Assignees */}
            <Box
              sx={{ display: "flex", cursor: "pointer" }}
              onClick={handleAssigneesClick}
            >
              {task?.assignTo?.length > 0 ? (
                <AvatarGroup
                  max={3}
                  sx={{
                    "& .MuiAvatar-root": {
                      width: 24,
                      height: 24,
                      fontSize: "11px",
                      border: "2px solid white",
                    },
                  }}
                >
                  {task.assignTo.map((item: any, index: number) => (
                    <Tooltip key={item?._id} title={item?.fullName}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          background: colorPalette[index % colorPalette.length],
                          fontSize: "0.875rem",
                        }}
                      >
                        {getInitials(item?.fullName)}
                      </Avatar>
                    </Tooltip>
                  ))}
                </AvatarGroup>
              ) : (
                <Chip
                  icon={<PersonOutlineIcon style={{ fontSize: "14px" }} />}
                  label={t("Unassigned")}
                  size="small"
                  variant="outlined"
                  sx={{ height: 24, fontSize: "12px" }}
                />
              )}
            </Box>

            {/* Recurring indicator */}
            {task?.isRecurring && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <Tooltip title={t("Recurring task")}>
                  <LoopOutlinedIcon
                    sx={{ fontSize: 14, color: "inherit", ml: 6 }}
                  />
                </Tooltip>
              </Box>
            )}

            {/* Date and comments */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mr: 1.5,
                  color: isDueDateApproaching() ? "#F44336" : "#757575",
                  backgroundColor: isDueDateApproaching()
                    ? "rgba(244, 67, 54, 0.08)"
                    : "transparent",
                  borderRadius: "4px",
                  padding: "2px 4px",
                  cursor: "pointer",
                }}
                onClick={handleDueDateClick}
              >
                <CalendarMonthOutlinedIcon
                  sx={{ fontSize: 14, mr: 0.5, color: "inherit" }}
                />
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "inherit",
                    fontWeight: isDueDateApproaching() ? 500 : 400,
                  }}
                >
                  {task?.dueDate ? formatDate(task.dueDate) : t("Set date")}
                </Typography>
              </Box>

              <Tooltip title={t("Comments")}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                    borderRadius: "4px",
                    padding: "2px 4px",
                    cursor: "pointer",
                  }}
                >
                  <ChatBubbleOutlineIcon
                    sx={{ fontSize: 14, color: "inherit", mr: 0.5 }}
                  />
                  <Typography sx={{ fontSize: "12px", color: "inherit" }}>
                    {task?.commentCount || 0}
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
          </Box>

          {/* Subtasks indicator */}
          {subtasksCount > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mt: 1.5,
                color: "#757575",
                fontSize: "12px",
                backgroundColor: "#f5f5f5",
                padding: "4px 8px",
                borderRadius: "4px",
                cursor: "pointer",
                userSelect: "none",
                "&:hover": {
                  backgroundColor: "#e0e0e0",
                },
              }}
            >
              <KeyboardArrowDownIcon fontSize="small" sx={{ mr: 0.5 }} />
              {subtasksCount} {t("subtask")}
              {subtasksCount !== 1 ? "s" : ""}
            </Box>
          )}

          {/* Project name */}
          {task?.project?.name && (
            <Box sx={{ mt: 1, borderTop: "1px solid #f0f0f0" }}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "10px",
                  color: "#757575",
                  fontWeight: 500,
                  fontStyle: "italic",
                  display: "block",
                }}
              >
                ~{task.project.name}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Priority Menu */}
      <Menu
        anchorEl={priorityAnchorEl}
        open={Boolean(priorityAnchorEl)}
        onClose={() => setPriorityAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <MenuItem
          onClick={() => handlePriorityChange("Highest")}
          selected={task?.severity === "Highest"}
        >
          <ListItemIcon>
            <FiberManualRecordIcon
              sx={{ color: severityColorMap["Highest"] }}
            />
          </ListItemIcon>
          <Typography variant="body2" color="error">
            {t("Highest")}
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => handlePriorityChange("High")}
          selected={task?.severity === "High"}
        >
          <ListItemIcon>
            <FiberManualRecordIcon sx={{ color: severityColorMap["High"] }} />
          </ListItemIcon>
          <Typography variant="body2" color="warning.dark">
            {t("High")}
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => handlePriorityChange("Medium")}
          selected={task?.severity === "Medium"}
        >
          <ListItemIcon>
            <FiberManualRecordIcon sx={{ color: severityColorMap["Medium"] }} />
          </ListItemIcon>
          <Typography variant="body2" color="info.main">
            {t("Medium")}
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => handlePriorityChange("Low")}
          selected={task?.severity === "Low"}
        >
          <ListItemIcon>
            <FiberManualRecordIcon sx={{ color: severityColorMap["Low"] }} />
          </ListItemIcon>
          <Typography variant="body2" color="success.main">
            {t("Low")}
          </Typography>
        </MenuItem>
      </Menu>

      {/* Assignee Popover - FIXED VERSION */}
      <Popover
        anchorEl={assigneeAnchorEl}
        open={Boolean(assigneeAnchorEl)}
        onClose={() => {
          setAssigneeAnchorEl(null);
          setSearchQuery(""); // Clear search when closing
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { width: 300, maxHeight: 400, p: 1 } }}
      >
        <Box sx={{ p: 1, mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              background: "#f5f5f5",
              borderRadius: 1,
              p: 0.5,
              mb: 1,
            }}
          >
            <Search sx={{ color: "text.secondary", ml: 1, mr: 1 }} />
            <InputBase
              placeholder={t("Search people...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ ml: 1, flex: 1 }}
              onClick={(e) => e.stopPropagation()}
            />
            {searchQuery && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchQuery("");
                }}
              >
                <Clear fontSize="small" />
              </IconButton>
            )}
          </Box>

          {/* Assigned Users Section */}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {t("Assigned")}
          </Typography>

          {task?.assignTo && task.assignTo.length > 0 ? (
            task.assignTo
              .filter((user: any) =>
                user.fullName.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map((user: any, index: number) => (
                <MenuItem
                  key={user._id}
                  onClick={() => handleAssigneeChange(user._id)}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        mr: 1,
                        background:
                          colorPalette[
                            task.assignTo.findIndex(
                              (u: any) => u._id === user._id
                            ) % colorPalette.length
                          ],
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontSize: "11px" }}>
                        {getInitials(user.fullName)}
                      </Typography>
                    </Avatar>
                    <Typography sx={{ flexGrow: 1, fontSize: "14px" }}>
                      {user.fullName}
                    </Typography>
                    <Checkbox checked={true} size="small" />
                  </Box>
                </MenuItem>
              ))
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ p: 1, fontSize: "12px", fontStyle: "italic" }}
            >
              {t("No users assigned")}
            </Typography>
          )}

          <Divider sx={{ my: 1 }} />

          {/* All Available Users Section */}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {t("Suggestions")}
          </Typography>

          {filteredUsers.length > 0 ? (
            filteredUsers.map((user: any, index: number) => {
              // Skip if already assigned
              if (task?.assignTo?.some((u: any) => u._id === user._id))
                return null;

              return (
                <MenuItem
                  key={user._id}
                  onClick={() => handleAssigneeChange(user._id)}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        mr: 1,
                        background: colorPalette[index % colorPalette.length],
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontSize: "11px" }}>
                        {getInitials(user.fullName)}
                      </Typography>
                    </Avatar>
                    <Typography sx={{ flexGrow: 1, fontSize: "14px" }}>
                      {user.fullName}
                    </Typography>
                    <Checkbox checked={false} size="small" />
                  </Box>
                </MenuItem>
              );
            })
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ p: 1, fontSize: "12px", fontStyle: "italic" }}
            >
              {searchQuery
                ? t("No users found")
                : t("No additional users available")}
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
};

// --- Droppable Column Component ---

const DroppableColumn: React.FC<DroppableColumnProps> = ({
  id,
  title,
  tasks,
  isLoading = false,
  isLoadingMore = false,
  hasMore = false,
  totalCount,
  onTaskClick,
  onMenuClick,
  onCreateTask,
  onLoadMore,
  headerActions,
  emptyMessage = "No tasks",
  sprint,
  isBacklog = false,
  color,
  canEditColumn = false,
  onTitleChange,
  onDelete,
  dragHandleProps,
  availableStatus,
  onTaskUpdate,
  refreshColumn,
  companyMemberData,
  isMinimized = false,
  onToggleMinimize,
  isDragging = false,
  sprints,
  containerId,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(["bugherd"]);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [titleValue, setTitleValue] = useState<string>(title);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { setNodeRef, isOver } = useDroppable({
    id,
    data: { type: "column", sprint, isBacklog },
  });

  const taskIds = useMemo(() => tasks.map((task) => task._id), [tasks]);

  useEffect(() => {
    setTitleValue(title);
  }, [title]);

  const handleScroll = useCallback(() => {
    // BLOCK SCROLL LOGIC IF DRAGGING
    if (isDragging) return;

    if (!hasMore) return;
    if (!containerRef.current || !onLoadMore) return;

    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);

    scrollTimeoutRef.current = setTimeout(() => {
      if (!containerRef.current || isDragging) return;

      if (!hasMore) return;

      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (scrollHeight <= clientHeight) return;
      if (isLoadingMore || isLoading) return;

      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      if (isNearBottom) {
        onLoadMore(id);
      }
    }, 200);
  }, [
    id,
    onLoadMore,
    isLoadingMore,
    isLoading,
    hasMore,
    tasks.length,
    isDragging,
  ]);

  useEffect(() => {
    const currentRef = containerRef.current;
    if (currentRef && !isMinimized) {
      currentRef.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll, isMinimized]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleRenameClick = () => {
    if (!canEditColumn) {
      handleMenuClose();
      return;
    }
    handleMenuClose();
    setTimeout(() => setIsEditing(true), 100);
  };
  const handleDeleteClick = () => {
    if (!canEditColumn || !onDelete) {
      handleMenuClose();
      return;
    }
    onDelete();
    handleMenuClose();
  };

  const handleTitleSubmit = () => {
    if (!canEditColumn || !onTitleChange) {
      setTitleValue(title);
      setIsEditing(false);
      return;
    }
    if (titleValue.trim() && titleValue !== title) onTitleChange(titleValue);
    else setTitleValue(title);
    setIsEditing(false);
  };
  const handleToggleMinimize = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleMinimize) onToggleMinimize();
  };
  const handleMinimizedColumnClick = () => {
    if (onToggleMinimize && isMinimized) onToggleMinimize();
  };
  const getColumnHeaderColor = () => color || "#4285F2";
  const getDisplayCount = () => {
    const fetchedCount = tasks?.length || 0;
    const total = totalCount;
    if (isLoading && fetchedCount === 0) return "...";
    if (total !== undefined && total !== null)
      return `${fetchedCount}/${total}`;
    return fetchedCount.toString();
  };
  const showFullSkeleton = isLoading && tasks.length === 0;
  const renderSkeletonCards = () =>
    Array(4)
      .fill(0)
      .map((_, index) => (
        <Skeleton
          key={`skeleton-${index}`}
          variant="rectangular"
          height={85}
          sx={{ mb: 1, borderRadius: 1, backgroundColor: "rgba(0,0,0,0.06)" }}
        />
      ));

  const columnStyles = {
    width: isMinimized ? 40 : 320,
    minWidth: isMinimized ? 40 : 320,
    maxWidth: isMinimized ? 40 : "none",
    display: "flex",
    flexDirection: "column" as const,
    height: "100%",
    transition: "all 0.3s ease",
    flexShrink: 0,
    margin: 0,
    padding: 0,
  };

  return (
    <Box sx={columnStyles} {...(canEditColumn ? dragHandleProps : {})}>
      {/* Header */}
      {showFullSkeleton ? (
        <Skeleton
          variant="rectangular"
          height={isMinimized ? 160 : 56}
          sx={{
            mb: isMinimized ? 0 : 1,
            borderRadius: isMinimized ? "16px" : "28px",
            backgroundColor: "rgba(0, 0, 0, 0.06)",
          }}
        />
      ) : (
        <Paper
          elevation={0}
          onClick={isMinimized ? handleMinimizedColumnClick : undefined}
          sx={{
            backgroundColor: getColumnHeaderColor(),
            p: isMinimized ? 0.5 : 1,
            mb: isMinimized ? 0 : 1,
            borderRadius: isMinimized ? "16px" : "28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            color: "white",
            cursor: isMinimized ? "pointer" : "default",
            transition: "all 0.3s ease",
            height: "auto",
            width: "100%",
            boxSizing: "border-box",
            position: "relative",
          }}
        >
          {canEditColumn && !isMinimized && dragHandleProps && (
            <Box
              {...dragHandleProps}
              {...(dragHandleProps.dragListeners || {})}
              data-drag-handle="column"
              sx={{
                position: "absolute",
                left: 4,
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "grab",
                display: "flex",
                alignItems: "center",
                color: "white",
                zIndex: 10,
                padding: "4px",
                borderRadius: "4px",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                "&:active": { cursor: "grabbing" },
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              <DragIndicatorIcon sx={{ fontSize: 16 }} />
            </Box>
          )}

          {isMinimized ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                py: 1,
                px: 0.5,
                height: "auto",
              }}
            >
              <Box
                sx={{
                  backgroundColor: "rgba(255,255,255,0.9)",
                  borderRadius: "50%",
                  width: 22,
                  height: 22,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "8px",
                    fontWeight: 500,
                    color: theme.palette.text.secondary,
                  }}
                >
                  {getDisplayCount()}
                </Typography>
              </Box>

              <Tooltip title={title} placement="right">
                <Typography
                  sx={{
                    writingMode: "vertical-rl",
                    transform: "rotate(180deg)",
                    fontWeight: 500,
                    fontSize: "11px",
                    textAlign: "center",
                    my: 1,
                    maxHeight: 120,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: "white",
                  }}
                >
                  {title}
                </Typography>
              </Tooltip>

              <IconButton
                size="small"
                onClick={handleToggleMinimize}
                sx={{
                  color: "white",
                  mt: 1,
                  padding: 0.2,
                  "& svg": {
                    fontSize: "0.85rem",
                  },
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexGrow: 1,
                  ml: canEditColumn && dragHandleProps ? 3 : 0,
                  pointerEvents: "auto",
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    borderRadius: "50%",
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "8px",
                      fontWeight: 500,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {getDisplayCount()}
                  </Typography>
                </Box>

                {isEditing && canEditColumn ? (
                  <TextField
                    size="small"
                    value={titleValue}
                    onChange={(e) => setTitleValue(e.target.value)}
                    autoFocus
                    onBlur={handleTitleSubmit}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleTitleSubmit();
                      }
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        color: "white",
                      },
                      flexGrow: 1,
                    }}
                  />
                ) : (
                  <Tooltip title={title}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 500,
                        fontSize: "14px",
                        cursor: canEditColumn ? "pointer" : "default",
                        flexGrow: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: 150,
                        color: "white",
                      }}
                      onClick={
                        canEditColumn ? () => setIsEditing(true) : undefined
                      }
                    >
                      {title}
                    </Typography>
                  </Tooltip>
                )}
              </Box>

              <Box sx={{ display: "flex", alignItems: "center" }}>
                {headerActions}
                <IconButton
                  size="small"
                  onClick={handleToggleMinimize}
                  sx={{ color: "white" }}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleMenuClick}
                  aria-controls={open ? "column-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                  sx={{ color: "white" }}
                >
                  <MoreVertIcon />
                </IconButton>
              </Box>
            </>
          )}

          <Menu
            id="column-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            MenuListProps={{
              "aria-labelledby": "column-menu-button",
            }}
          >
            <MenuItem
              onClick={handleRenameClick}
              disabled={!canEditColumn}
              sx={{ opacity: canEditColumn ? 1 : 0.5 }}
            >
              <EditIcon sx={{ mr: 1, fontSize: 16 }} />
              {t("Rename")}
              {!canEditColumn && (
                <Typography
                  variant="caption"
                  sx={{ ml: 1, color: "text.secondary" }}
                >
                  {t("(Status only)")}
                </Typography>
              )}
            </MenuItem>
            <MenuItem
              onClick={handleDeleteClick}
              disabled={!canEditColumn}
              sx={{ opacity: canEditColumn ? 1 : 0.5 }}
            >
              <DeleteIcon sx={{ mr: 1, fontSize: 16 }} />
              {t("Delete")}
              {!canEditColumn && (
                <Typography
                  variant="caption"
                  sx={{ ml: 1, color: "text.secondary" }}
                >
                  {t("(Status only)")}
                </Typography>
              )}
            </MenuItem>
          </Menu>
        </Paper>
      )}

      {/* Sprint status and info */}
      {!isMinimized && !showFullSkeleton && sprint && (
        <Box
          sx={{
            px: 1,
            pb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
          }}
        >
          <Chip
            label={sprint.status}
            size="small"
            color={sprint.status === "ACTIVE" ? "primary" : "default"}
            variant="outlined"
            sx={{ fontSize: "0.7rem", height: 20 }}
          />
          {sprint.status === "ACTIVE" && sprint.startDate && sprint.endDate && (
            <Typography variant="caption" color="text.secondary">
              {new Date(sprint.startDate).toLocaleDateString()} -{" "}
              {new Date(sprint.endDate).toLocaleDateString()}
            </Typography>
          )}
        </Box>
      )}

      {/* Content */}
      {!isMinimized && (
        <Box
          ref={(el) => {
            setNodeRef(el);
            containerRef.current = el;
          }}
          sx={{
            flex: 1,
            p: 2,
            overflow: "auto",
            backgroundColor: isOver
              ? hexToRgba("#4285F4", 0.08)
              : "transparent",
            border: isOver
              ? `1px dashed ${theme.palette.primary.main}`
              : "1px solid transparent",
            transition: "background-color 0.2s ease",
            borderRadius: 1,
            display: "flex",
            flexDirection: "column",
            width: "100%",
            position: "relative",
            pointerEvents: "auto",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: theme.palette.grey[100],
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: theme.palette.grey[400],
              borderRadius: "3px",
              "&:hover": {
                background: theme.palette.grey[600],
              },
            },
          }}
          onMouseDown={(e) => {
            const isDragHandle = (e.target as HTMLElement).closest(
              '[data-drag-handle="column"]'
            );
            if (!isDragHandle) {
              e.stopPropagation();
            }
          }}
        >
          {showFullSkeleton ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                width: "100%",
              }}
            >
              {renderSkeletonCards()}
            </Box>
          ) : (
            <>
              <SortableContext
                items={taskIds}
                strategy={verticalListSortingStrategy}
              >
                {tasks.map((task) => (
                  <TaskCard
                    key={task._id}
                    task={task}
                    onTaskClick={onTaskClick}
                    onMenuClick={onMenuClick}
                    availableStatus={availableStatus}
                    onTaskUpdate={onTaskUpdate}
                    refreshColumn={refreshColumn}
                    companyMemberData={companyMemberData}
                    containerId={containerId}
                  />
                ))}
              </SortableContext>

              {isLoadingMore && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    py: 2,
                    mt: 1,
                    mb: "88px",
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    borderRadius: 1,
                    border: "1px solid rgba(0, 0, 0, 0.1)",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <CircularProgress
                    size={20}
                    thickness={4}
                    sx={{ color: "primary.main", mr: 1 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: "12px",
                      fontWeight: 500,
                    }}
                  >
                    {t("Loading more tasks...")}
                  </Typography>
                </Box>
              )}

              {!isLoadingMore && !isLoading && !hasMore && tasks.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 1,
                    mt: 1,
                    mb: "88px",
                    color: "text.secondary",
                    backgroundColor: "rgba(0, 0, 0, 0.03)",
                    borderRadius: 1,
                    border: "1px dashed rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: "11px" }}>
                    {t("No more items to load")}
                  </Typography>
                </Box>
              )}

              {!isLoading && !isLoadingMore && tasks.length === 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    py: 4,
                    px: 2,
                    color: "text.secondary",
                    backgroundColor: "rgba(0, 0, 0, 0.02)",
                    borderRadius: 1,
                    border: "1px dashed rgba(0, 0, 0, 0.08)",
                    gap: 2,
                  }}
                >
                  {isBacklog && sprints?.length === 0 ? (
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "14px",
                          textAlign: "center",
                          fontWeight: "bold",
                        }}
                      >
                        {t("Get started in backlog")}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "14px", textAlign: "center" }}
                      >
                        {t("Plan and start a sprint to see work here.")}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ fontSize: "12px" }}>
                      {emptyMessage}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Create task button */}
              {!showFullSkeleton &&
                !isLoadingMore &&
                !hasMore &&
                onCreateTask && (
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onCreateTask();
                    }}
                    sx={{
                      mt: 2,
                      borderStyle: "dashed",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.secondary,
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                      },
                    }}
                  >
                    {t("Create task")}
                  </Button>
                )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

// --- Main Board View Component ---

const BacklogBoardView: React.FC<BacklogBoardViewProps> = ({
  backlogRows,
  sprints,
  sprintTasksData,
  isLoading,
  onTaskClick,
  onMenuClick,
  onCreateTask,
  onCreateSprint,
  onStartSprint,
  onCompleteSprint,
  handleDragStart,
  handleDragOver,
  handleDragEnd,
  activeTask,
  findTask,
  statusList = [],
  companyMemberData,
  onTaskUpdate,
  refreshColumn,
  minimizedColumns = {},
  onToggleMinimize,
  onLoadMore,
  columnPaginationState = {},
  backlogTotalCount,
  sprintTotalCounts = {},
}) => {
  const theme = useTheme();
  const { t } = useTranslation(["bugherd"]);
  const [isAnyDragging, setIsAnyDragging] = useState(false);

  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Sort sprints by creation date (newest first)
  const sortedSprints = useMemo(() => {
    return [...sprints].sort(
      (a, b) =>
        new Date(b.createdAt || 0).getTime() -
        new Date(a.createdAt || 0).getTime()
    );
  }, [sprints]);

  const handleDragStartWrapper = (event: DragStartEvent) => {
    setIsAnyDragging(true);
    handleDragStart(event);
  };

  const handleDragEndWrapper = (event: DragEndEvent) => {
    setIsAnyDragging(false);
    handleDragEnd(event);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStartWrapper}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEndWrapper}
    >
      <Box
        sx={{
          height: "calc(100vh - 250px)",
          display: "flex",
          overflow: "hidden",
          gap: 2,
          p: 2,
        }}
      >
        {/* Backlog Column - Fixed */}
        <Box sx={{ flexShrink: 0 }}>
          <DroppableColumn
            id="backlog"
            title={t("Backlog")}
            tasks={backlogRows}
            totalCount={backlogTotalCount || backlogRows.length}
            isLoading={isLoading && backlogRows.length === 0}
            isLoadingMore={columnPaginationState["backlog"]?.isLoadingMore}
            hasMore={backlogRows.length < (backlogTotalCount || 0)}
            onTaskClick={onTaskClick}
            onMenuClick={onMenuClick}
            onCreateTask={() => onCreateTask()}
            onLoadMore={onLoadMore}
            headerActions={
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={onCreateSprint}
                sx={{
                  fontSize: "0.75rem",
                  color: "white",
                  borderColor: "white",
                  fontWeight: "bold",
                }}
              >
                {t("Create Sprint")}
              </Button>
            }
            emptyMessage={t("No tasks in backlog")}
            isBacklog={true}
            color="#6366f1"
            availableStatus={statusList}
            onTaskUpdate={onTaskUpdate}
            refreshColumn={refreshColumn}
            companyMemberData={companyMemberData}
            isMinimized={minimizedColumns["backlog"]}
            onToggleMinimize={() => onToggleMinimize?.("backlog")}
            sprints={sortedSprints}
            isDragging={isAnyDragging}
            containerId="backlog"
          />
        </Box>

        {/* Sprints Container - Scrollable */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            gap: 2,
            overflow: "auto",
            "&::-webkit-scrollbar": {
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: theme.palette.grey[100],
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: theme.palette.grey[400],
              borderRadius: "4px",
              "&:hover": {
                background: theme.palette.grey[600],
              },
            },
          }}
        >
          {sortedSprints.length === 0 ? (
            <Box
              sx={{
                width: 320,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                border: `2px dashed ${theme.palette.divider}`,
                borderRadius: 1,
                color: theme.palette.text.secondary,
              }}
            >
              <Typography variant="body1" sx={{ mb: 2 }}>
                {t("No sprints created yet")}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onCreateSprint}
              >
                {t("Create First Sprint")}
              </Button>
            </Box>
          ) : (
            sortedSprints.map((sprint) => {
              const sprintId = `sprint-${sprint._id}`;
              const sprintData = sprintTasksData[sprint._id];
              const sprintTasks = sprintData?.data?.decryptedData?.items || [];
              const sprintTotal =
                sprintTotalCounts[sprint._id] !== undefined
                  ? sprintTotalCounts[sprint._id]
                  : sprintData?.data?.decryptedData?.total || 0;
              const isSprintLoading = !sprintData && isLoading;
              const paginationState = columnPaginationState[sprintId] || {
                isLoadingMore: false,
              };
              const hasMore = sprintTasks.length < sprintTotal;

              return (
                <DroppableColumn
                  key={sprint._id}
                  id={sprintId}
                  title={sprint.name}
                  tasks={sprintTasks}
                  totalCount={sprintTotal}
                  isLoading={isSprintLoading}
                  isLoadingMore={paginationState.isLoadingMore}
                  hasMore={hasMore}
                  onTaskClick={onTaskClick}
                  onMenuClick={onMenuClick}
                  onCreateTask={() => onCreateTask(sprint._id)}
                  onLoadMore={onLoadMore}
                  sprint={sprint}
                  headerActions={
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {sprint.status === "DRAFT" ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PlayArrowIcon />}
                          onClick={(e) => onStartSprint(e, sprint)}
                          disabled={sprintTasks.length === 0}
                          sx={{
                            fontSize: "0.75rem",
                            color: "white",
                            borderColor: "white",
                          }}
                        >
                          {t("Start")}
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CheckCircleIcon />}
                          onClick={(e) => onCompleteSprint(e, sprint)}
                          sx={{
                            fontSize: "0.75rem",
                            color: "white",
                            borderColor: "white",
                          }}
                        >
                          {t("Complete")}
                        </Button>
                      )}
                    </Box>
                  }
                  emptyMessage={t("No tasks in this sprint")}
                  color={sprint.status === "ACTIVE" ? "#4caf50" : "#ff9800"}
                  availableStatus={statusList}
                  onTaskUpdate={onTaskUpdate}
                  refreshColumn={refreshColumn}
                  companyMemberData={companyMemberData}
                  isMinimized={minimizedColumns[sprintId]}
                  onToggleMinimize={() =>
                    onToggleMinimize && onToggleMinimize(sprintId)
                  }
                  sprints={sprints}
                  isDragging={isAnyDragging}
                  containerId={sprintId}
                />
              );
            })
          )}
        </Box>

        <DragOverlay>
          {activeTask ? (
            <Paper
              sx={{
                width: 300,
                backgroundColor: theme.palette.background.paper,
                boxShadow: theme.shadows[8],
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: 1,
                opacity: 0.9,
              }}
            >
              <TaskCard
                task={activeTask}
                onTaskClick={() => {}}
                onMenuClick={() => {}}
                isDragging={true}
                availableStatus={statusList}
                companyMemberData={companyMemberData}
              />
            </Paper>
          ) : null}
        </DragOverlay>
      </Box>
    </DndContext>
  );
};

export default BacklogBoardView;
